// ModelManager.js
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MODEL_URL } from "../../helpers/CONSTANT";
import { VARS } from "../Global/variables";

export class ModelManager {
  constructor(scene, setModelProgress, currentDesign) {
    this.scene = scene;
    this.setModelProgress = setModelProgress;
    this.currentDesign = currentDesign;
    this.modelMeshs = [];

    this.boxMinz = 0;

    // Three.js loading manager for progress + error tracking
    this.loadingManager = new THREE.LoadingManager();
    this.loadingManager.onProgress = this.onProgress.bind(this);
    this.loadingManager.onError = this.onError.bind(this);

    // Decide source based on env
    // CRA env vars come as strings
    this.isDevMode = (process.env.REACT_APP_DEV_MODE || "").toLowerCase() === "true";

    // Local model path (served from /public)
    // Put your file here: public/local_model/model.glb
    this.localModelPath = "/local_model/model.glb";

    this.loadModel();
    this.addFloor();
    this.addLights();

    return this.modelMeshs;
  }

  loadModel() {
    const loader = new GLTFLoader(this.loadingManager);

    // Choose which model URL to load
    const modelUrl = this.isDevMode ? this.localModelPath : this.currentDesign?.model;

    if (!modelUrl) {
      console.error("❌ Model URL is missing. currentDesign?.model is empty and dev mode is off.");
      return;
    }

    loader.load(
      modelUrl,
      (gltf) => {
        const root = gltf.scene;

        // Normalize Z so model sits on floor
        this.setModel(root);

        // Store all nodes/meshes
        root.traverse((child) => {
          this.modelMeshs.push(child);
          VARS.Meshes.push(child);
        });

        root.name = "houseModel";
        this.scene.add(root);
      },
      undefined,
      (err) => {
        console.error(`❌ Failed to load model: ${modelUrl}`, err);
      }
    );
  }

  // Normalize model vertical position so the base touches the floor
  setModel(root) {
    const bbox = new THREE.Box3().setFromObject(root);
    this.boxMinz = bbox.min.z;
    root.position.z -= this.boxMinz;
  }

  addFloor() {
    const geometry = new THREE.BoxGeometry(130, 130, 0);

    // Keeping your existing floor logic
    // (You can also make this env-based if you want)
    const address = this.isDevMode ? MODEL_URL : this.currentDesign?.address_image;

    const texture = new THREE.TextureLoader().load(address);
    const material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.8 });

    const cube = new THREE.Mesh(geometry, material);
    cube.userData.name = "ground";
    cube.position.set(0, 0, 0);
    this.scene.add(cube);

    VARS.mapTile = cube;

    const renderTarget = new THREE.WebGLRenderTarget(window.innerWidth, window.innerHeight);
    renderTarget.texture.colorSpace = THREE.SRGBColorSpace;
  }

  addLights() {
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.75);
    directionalLight.position.set(0, 0, -2);
    this.scene.add(directionalLight);
  }

  // LoadingManager progress signature: (url, itemsLoaded, itemsTotal)
  onProgress(url, loaded, total) {
    const progress = (loaded / total) * 100;
    this.setModelProgress(progress.toFixed(2));
  }

  onError(url) {
    console.error(`Error loading ${url}`);
  }
}
